/**
 * /producao/ambientes/:id — Detalhe do Ambiente (Sprint P1).
 *
 * Tela mais "generosa" do módulo (brief "Tela — Detalhe do Ambiente"):
 *   Zona 1 — EntityHeader com voltar ao Painel + breadcrumb
 *            `Produção / {pedido} / {numero}` + badges (fase, congelado,
 *            cancelado) + ações (Congelar/Descongelar, Registrar atraso,
 *            Cancelar — gates RBAC por ação).
 *   Zona 2 — KpiStrip (fase atual + progresso + buffer + atraso acumulado
 *            + peças) e edição inline da data-alvo de carregamento (PATCH).
 *   Zona 3 — Linha do tempo fase→etapas (grupos expansíveis + colunas
 *            nomeadas + ações Iniciar/Concluir), Atrasos por etapa+causa,
 *            banner de congelamento/cancelamento, blocos de Peças/Bipagens
 *            (P3) e Linha do tempo agregada de eventos (Lote A —
 *            _ambiente/Timeline).
 *
 * Toda action retorna o Detalhe atualizado — a tela substitui o estado com
 * a resposta (sem refetch extra). Datas SEMPRE em `dd/mmm hh:mm` (F.2).
 * Cancelar exige MFA recente — o 403 é tratado pelo modal global de step-up
 * do AuthContext (retry transparente).
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiAlertTriangle,
  FiChevronDown,
  FiChevronRight,
  FiClock,
  FiCloudSnow,
  FiSun,
  FiXCircle,
} from "react-icons/fi";
import styled from "styled-components";

import AsyncButton from "../../components/AsyncButton";
import EntityHeader from "../../components/EntityHeader";
import KpiStrip from "../../components/KpiStrip";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  BUFFER_ZONA_TONES,
  CATEGORIAS_6M,
  ESTADO_ETAPA_LABELS,
  ESTADO_ETAPA_TONES,
  FASE_INFO,
  MOTIVOS_CONGELAMENTO,
  atualizarAmbiente,
  cancelarAmbiente,
  concluirEtapa,
  congelarAmbiente,
  descongelarAmbiente,
  erroEhCheckpointBipagem,
  iniciarEtapa,
  obterAmbiente,
  permissaoExtraConcluirEtapa,
  permissaoOperarFase,
  registrarAtrasoAmbiente,
} from "../../services/producaoV2";
import { useCheckpointsBipagem } from "./_shared/useCheckpointsBipagem";
import { useConfigProducao } from "./_shared/useConfigProducao";
import { CATEGORIA_LABELS } from "../../services/terceirizacao";
import {
  formatarDataCurta,
  formatarDataHoraCurta,
  formatarHorasMinutos,
  formatarMinutosAtraso,
} from "../../utils/dataHora";
import BomBloco from "./_ambiente/BomBloco";
import Timeline from "./_ambiente/Timeline";
import PecasDoAmbiente from "./PecasDoAmbiente";
import { PageContainer } from "../../styles/shared";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Pill,
  SecondaryButton,
  SectionTitle,
  Select,
  WarningMsg,
} from "../Orcamentos/styles";

const ESTADO_PENDENTE = "PENDENTE";
const ESTADO_EM_EXECUCAO = "EM_EXECUCAO";
const ESTADO_CONCLUIDA = "CONCLUIDA";
const ESTADO_NAO_APLICAVEL = "NAO_APLICAVEL";
const FASE_CARREGADO = "CARREGADO";

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Mono = styled.span`
  font-variant-numeric: tabular-nums;
`;

/**
 * Wrapper responsivo da tela (Lote B — mobile do responsável de setor).
 * <768px: KPIs em 2 colunas (empilhados), tabelas viram lista (regra na
 * TabelaBloco) e botões de ação crescem para alvo de toque >=44px.
 * Overrides via descendant selector — KpiStrip/PageContainer são shared
 * e não mudam.
 */
const PaginaDetalhe = styled(PageContainer)`
  @media (max-width: 768px) {
    .kpi-strip-v2 {
      grid-template-columns: repeat(2, 1fr) !important;
    }
  }
`;

const TabelaBloco = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-3);
    padding: 8px 10px;
    border-bottom: 1px solid var(--border);
  }

  td {
    padding: 8px 10px;
    border-top: 1px solid var(--border-subtle);
    color: var(--ink-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  tr.fase td {
    background: var(--surface-2);
    color: var(--ink);
    font-weight: 600;
    font-size: 12px;
    cursor: pointer;
    user-select: none;
  }

  tr.na td {
    color: var(--ink-4);
  }

  td.acoes {
    overflow: visible;
    white-space: nowrap;
  }

  /* ---- <768px: tabela vira LISTA (zero scroll lateral em mobile) ----
     Cada <tr> vira um bloco; células com data-rotulo ganham o rótulo
     inline (o <thead> some); células vazias somem. */
  @media (max-width: 768px) {
    &,
    tbody,
    tr,
    td {
      display: block;
    }
    colgroup,
    thead {
      display: none;
    }
    tr {
      border-top: 1px solid var(--border-subtle);
      padding: 10px 2px;
    }
    tr.fase {
      border-top: none;
      border-radius: 8px;
      padding: 10px 8px;
    }
    tr.fase td {
      padding: 0;
    }
    td {
      border: none;
      padding: 2px 0;
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
    }
    td:empty {
      display: none;
    }
    td[data-rotulo]::before {
      content: attr(data-rotulo) ": ";
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--ink-3);
    }
    td.acoes {
      padding-top: 8px;
    }
  }
`;

const ResumoFase = styled.span`
  margin-left: 10px;
  font-weight: 500;
  font-size: 11px;
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
`;

const AcoesLinha = styled.div`
  display: inline-flex;
  gap: 6px;

  /* Densidade desktop dos botões de etapa (antes era style inline —
     migrado p/ CSS para a media query mobile poder vencer). */
  button {
    padding: 3px 10px;
    font-size: 0.75rem;
  }

  @media (max-width: 768px) {
    width: 100%;
    gap: 10px;

    button {
      flex: 1;
      min-height: 44px;
      padding: 10px 16px;
      font-size: 0.85rem;
    }
  }
`;

const BannerConteudo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  strong {
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

const LinhaDado = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const RotuloCampo = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-2);
`;

const DicaCampo = styled.p`
  font-size: 12px;
  color: var(--ink-3);
  margin: 4px 0 0;
`;

const CampoForm = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const CampoInput = styled.input`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  font-size: 0.88rem;
  font-family: inherit;

  &:focus {
    outline: 2px solid var(--accent);
    outline-offset: -1px;
    border-color: transparent;
  }
`;

const AreaTexto = styled.textarea`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  font-size: 0.88rem;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: 2px solid var(--accent);
    outline-offset: -1px;
    border-color: transparent;
  }
`;

const RodapePainel = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
`;

const LinhaErro = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

// ---------------------------------------------------------------------------
// Slide-overs de ação
// ---------------------------------------------------------------------------

function CongelarForm({ aberto, onFechar, ambienteId, onAtualizado }) {
  const { request } = useAuth();
  const [motivo, setMotivo] = useState("");
  const [observacao, setObservacao] = useState("");

  useEffect(() => {
    if (aberto) {
      setMotivo("");
      setObservacao("");
    }
  }, [aberto]);

  const info = MOTIVOS_CONGELAMENTO.find((m) => m.codigo === motivo);

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Congelar ambiente"
      subtitle="Congelado, o ambiente não aceita operação de etapas até descongelar"
      footer={
        <RodapePainel>
          <SecondaryButton type="button" onClick={onFechar}>
            Voltar
          </SecondaryButton>
          <AsyncButton
            disabled={!motivo}
            silentError
            successMessage="Ambiente congelado."
            onClick={async () => {
              const resp = await congelarAmbiente(request, ambienteId, {
                motivo,
                observacao,
              });
              onAtualizado(resp);
              onFechar();
            }}
          >
            Congelar ambiente
          </AsyncButton>
        </RodapePainel>
      }
    >
      <CampoForm>
        <RotuloCampo>Motivo *</RotuloCampo>
        <Select value={motivo} onChange={(e) => setMotivo(e.target.value)}>
          <option value="">Selecione o motivo…</option>
          {MOTIVOS_CONGELAMENTO.map((m) => (
            <option key={m.codigo} value={m.codigo}>
              {m.label}
            </option>
          ))}
        </Select>
        {info && (
          <DicaCampo>
            {info.ttlDias
              ? `Prazo padrão: ${info.ttlDias} dias. Vencido, o ambiente NÃO descongela sozinho — o descongelamento é manual.`
              : "Sem prazo padrão — aguarda o retorno do terceiro (OSE)."}
          </DicaCampo>
        )}
      </CampoForm>
      <CampoForm>
        <RotuloCampo>Observação</RotuloCampo>
        <AreaTexto
          rows={3}
          maxLength={300}
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="Contexto do congelamento (opcional)"
        />
      </CampoForm>
    </SlideOverPanel>
  );
}

function RegistrarAtrasoForm({
  aberto, onFechar, ambienteId, etapas, onAtualizado,
}) {
  const { request } = useAuth();
  const [minutos, setMinutos] = useState("");
  const [categoria, setCategoria] = useState("");
  const [etapaId, setEtapaId] = useState("");
  const [descricao, setDescricao] = useState("");
  const [fornecedorId, setFornecedorId] = useState("");
  // null = ainda não carregado; [] = carregado (vazio ou indisponível).
  const [parceiros, setParceiros] = useState(null);

  useEffect(() => {
    if (aberto) {
      setMinutos("");
      setCategoria("");
      setEtapaId("");
      setDescricao("");
      setFornecedorId("");
    }
  }, [aberto]);

  // Fornecedores (C7): carrega 1x na primeira abertura; silentError — sem
  // a lista (ex.: sem permissão parceiro:view) o select degrada desativado
  // e o resto do form segue funcionando.
  useEffect(() => {
    if (!aberto || parceiros !== null) return undefined;
    let ativo = true;
    request("/api/parceiros?ativo=true", { silentError: true })
      .then((p) => {
        if (!ativo) return;
        const lista = Array.isArray(p) ? p : p?.results || [];
        setParceiros(Array.isArray(lista) ? lista : []);
      })
      .catch(() => {
        if (ativo) setParceiros([]);
      });
    return () => {
      ativo = false;
    };
  }, [aberto, parceiros, request]);

  const valido = Number(minutos) > 0 && Boolean(categoria);

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Registrar atraso"
      subtitle="Causa 6M obrigatória — alimenta o Ishikawa e o consumo de buffer"
      footer={
        <RodapePainel>
          <SecondaryButton type="button" onClick={onFechar}>
            Voltar
          </SecondaryButton>
          <AsyncButton
            disabled={!valido}
            silentError
            successMessage="Atraso registrado."
            onClick={async () => {
              const resp = await registrarAtrasoAmbiente(request, ambienteId, {
                minutos: Number(minutos),
                categoria,
                etapaId: etapaId || undefined,
                descricao,
                fornecedorId: fornecedorId || undefined,
              });
              onAtualizado(resp);
              onFechar();
            }}
          >
            Registrar atraso
          </AsyncButton>
        </RodapePainel>
      }
    >
      <CampoForm>
        <RotuloCampo>Minutos de atraso *</RotuloCampo>
        <CampoInput
          type="number"
          min={1}
          step={1}
          value={minutos}
          onChange={(e) => setMinutos(e.target.value)}
          placeholder="Ex.: 90"
        />
      </CampoForm>
      <CampoForm>
        <RotuloCampo>Categoria da causa (6M) *</RotuloCampo>
        <Select value={categoria} onChange={(e) => setCategoria(e.target.value)}>
          <option value="">Selecione a categoria…</option>
          {CATEGORIAS_6M.map((c) => (
            <option key={c.codigo} value={c.codigo}>
              {c.label}
            </option>
          ))}
        </Select>
      </CampoForm>
      <CampoForm>
        <RotuloCampo>Fornecedor (opcional)</RotuloCampo>
        <Select
          value={fornecedorId}
          disabled={parceiros !== null && parceiros.length === 0}
          title={
            parceiros !== null && parceiros.length === 0
              ? "Nenhum parceiro ativo disponível"
              : undefined
          }
          onChange={(e) => setFornecedorId(e.target.value)}
        >
          <option value="">Sem fornecedor vinculado</option>
          {(parceiros || []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.nome}
              {p.categoria
                ? ` — ${CATEGORIA_LABELS[p.categoria] || p.categoria}`
                : ""}
            </option>
          ))}
        </Select>
        <DicaCampo>
          Informe quando a causa é de terceiro (meio externo).
        </DicaCampo>
      </CampoForm>
      <CampoForm>
        <RotuloCampo>Etapa (opcional)</RotuloCampo>
        <Select value={etapaId} onChange={(e) => setEtapaId(e.target.value)}>
          <option value="">Ambiente inteiro (sem etapa específica)</option>
          {etapas.map((e) => (
            <option key={e.id} value={e.id}>
              {e.label} ({FASE_INFO[e.fase]?.curto || e.fase})
            </option>
          ))}
        </Select>
      </CampoForm>
      <CampoForm>
        <RotuloCampo>Descrição</RotuloCampo>
        <AreaTexto
          rows={3}
          maxLength={300}
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          placeholder="O que aconteceu? (opcional)"
        />
      </CampoForm>
    </SlideOverPanel>
  );
}

function CancelarForm({ aberto, onFechar, ambiente, onAtualizado }) {
  const { request } = useAuth();
  const [motivo, setMotivo] = useState("");
  // D10 (decisão do dono, 2026-08-11): o mesmo botão servia a duas
  // situações opostas. "Cliente desistiu" reduz o escopo — o pedido tem
  // de valer menos. "Problema de produção" não: o cliente continua
  // devendo o valor cheio, e baixá-lo seria presenteá-lo por um erro
  // nosso. Sem escolher, não dá para cancelar.
  const [natureza, setNatureza] = useState("");

  useEffect(() => {
    if (aberto) {
      setMotivo("");
      setNatureza("");
    }
  }, [aberto]);

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Cancelar ambiente"
      subtitle={ambiente ? `${ambiente.numero} · ${ambiente.nome}` : undefined}
      footer={
        <RodapePainel>
          <SecondaryButton type="button" onClick={onFechar}>
            Voltar
          </SecondaryButton>
          {/* Exige MFA recente: o 403 do backend abre o modal global de
              step-up e a request é refeita automaticamente após o TOTP. */}
          <AsyncButton
            variant="danger"
            disabled={!motivo.trim() || !natureza}
            silentError
            successMessage={
              natureza === "CLIENTE_DESISTIU"
                ? "Ambiente cancelado. Revisão do pedido aberta."
                : "Ambiente cancelado."
            }
            onClick={async () => {
              const resp = await cancelarAmbiente(
                request, ambiente.id, motivo.trim(), natureza,
              );
              onAtualizado(resp);
              onFechar();
            }}
          >
            Cancelar ambiente
          </AsyncButton>
        </RodapePainel>
      }
    >
      <ErrorMsg style={{ marginBottom: 16 }}>
        Ação irreversível: o ambiente sai do fluxo de produção e as peças
        vinculadas são canceladas em cascata. Exige verificação MFA recente.
      </ErrorMsg>
      <CampoForm>
        <RotuloCampo>Por que está sendo cancelado? *</RotuloCampo>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[
            {
              valor: "CLIENTE_DESISTIU",
              titulo: "O cliente desistiu deste ambiente",
              ajuda:
                "O escopo diminuiu. Vamos abrir a revisão do pedido para o "
                + "valor e as parcelas acompanharem — o cliente não paga por "
                + "um ambiente que não vai receber.",
            },
            {
              valor: "PROBLEMA_PRODUCAO",
              titulo: "Problema de produção (vamos refazer)",
              ajuda:
                "O escopo é o mesmo. O valor do pedido NÃO muda: o cliente "
                + "continua recebendo o ambiente e devendo o valor cheio.",
            },
          ].map((op) => (
            <label
              key={op.valor}
              style={{
                display: "flex",
                gap: 10,
                padding: "10px 12px",
                border: "1px solid var(--border)",
                borderRadius: 6,
                cursor: "pointer",
                background:
                  natureza === op.valor ? "var(--info-bg)" : "transparent",
              }}
            >
              <input
                type="radio"
                name="natureza-cancelamento"
                value={op.valor}
                checked={natureza === op.valor}
                onChange={() => setNatureza(op.valor)}
                style={{ marginTop: 3 }}
              />
              <span>
                <strong style={{ display: "block" }}>{op.titulo}</strong>
                <span style={{ fontSize: 12, opacity: 0.75 }}>{op.ajuda}</span>
              </span>
            </label>
          ))}
        </div>
      </CampoForm>
      <CampoForm>
        <RotuloCampo>Motivo do cancelamento *</RotuloCampo>
        <AreaTexto
          rows={3}
          maxLength={300}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Por que este ambiente está sendo cancelado?"
        />
      </CampoForm>
    </SlideOverPanel>
  );
}

// ---------------------------------------------------------------------------
// Tela
// ---------------------------------------------------------------------------

export default function DetalheAmbiente() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { request, hasPermission } = useAuth();

  const [ambiente, setAmbiente] = useState(null);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [painelAcao, setPainelAcao] = useState(null); // congelar|atraso|cancelar
  const [fasesFechadas, setFasesFechadas] = useState(() => new Set());
  const [dataAlvo, setDataAlvo] = useState("");
  // Erro inline das ações de etapa (Iniciar/Concluir) — o request roda com
  // silentError e a mensagem aparece amigável acima da linha do tempo.
  const [erroEtapa, setErroEtapa] = useState(null);
  const checkpoints = useCheckpointsBipagem();
  const { valorDe: valorConfigDe } = useConfigProducao();

  const aplicarAmbiente = useCallback((a) => {
    setAmbiente(a);
    setDataAlvo(a?.data_alvo_carregamento || "");
  }, []);

  const carregar = useCallback(async () => {
    setErro(null);
    setCarregando(true);
    try {
      aplicarAmbiente(await obterAmbiente(request, id));
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar o ambiente.");
    } finally {
      setCarregando(false);
    }
  }, [request, id, aplicarAmbiente]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // ---- gates RBAC (F.5 — sem permissão, ação oculta) ----
  const podeCongelar = hasPermission("ambiente_producao:congelar");
  const podeDescongelar = hasPermission("ambiente_producao:descongelar");
  const podeRegistrarCausa = hasPermission("ambiente_producao:registrar_causa");
  const podeCancelar = hasPermission("ambiente_producao:cancelar");
  const podeEditar = hasPermission("ambiente_producao:edit");

  // Lote B (D1.1): operar etapa é por FASE — producao_fase:operar_f1..f6
  // (Master bypass dentro do hasPermission). Substitui o gate único
  // ambiente_producao:avancar_etapa nas ações de Iniciar/Concluir.
  const podeOperarFaseDe = useCallback(
    (fase) => {
      const permissao = permissaoOperarFase(fase);
      return Boolean(permissao) && hasPermission(permissao);
    },
    [hasPermission],
  );

  // ---- derivações do payload ----
  const derivado = useMemo(() => {
    const etapas = [...(ambiente?.etapas || [])].sort(
      (a, b) => (a.ordem || 0) - (b.ordem || 0),
    );
    const aplicaveis = etapas.filter((e) => e.estado !== ESTADO_NAO_APLICAVEL);
    const concluidas = aplicaveis.filter(
      (e) => e.estado === ESTADO_CONCLUIDA,
    ).length;
    const proxima = aplicaveis.find((e) => e.estado !== ESTADO_CONCLUIDA);

    const grupos = [];
    const porFase = new Map();
    for (const etapa of etapas) {
      if (!porFase.has(etapa.fase)) {
        const grupo = {
          fase: etapa.fase,
          rotulo: FASE_INFO[etapa.fase]?.rotulo || etapa.fase,
          etapas: [],
          aplicaveis: 0,
          concluidas: 0,
        };
        porFase.set(etapa.fase, grupo);
        grupos.push(grupo);
      }
      const grupo = porFase.get(etapa.fase);
      grupo.etapas.push(etapa);
      if (etapa.estado !== ESTADO_NAO_APLICAVEL) {
        grupo.aplicaveis += 1;
        if (etapa.estado === ESTADO_CONCLUIDA) grupo.concluidas += 1;
      }
    }

    const rotuloEtapa = {};
    etapas.forEach((e) => {
      rotuloEtapa[e.codigo] = e.label;
    });

    const totalAtrasoMin = (ambiente?.atrasos || []).reduce(
      (soma, a) => soma + (Number(a.minutos) || 0),
      0,
    );

    return {
      etapas, aplicaveis, concluidas, proxima, grupos, rotuloEtapa,
      totalAtrasoMin,
    };
  }, [ambiente]);

  const acoesBloqueadas =
    !ambiente || ambiente.cancelado || ambiente.fase === FASE_CARREGADO;

  const alternarFase = (fase) =>
    setFasesFechadas((prev) => {
      const proximo = new Set(prev);
      if (proximo.has(fase)) proximo.delete(fase);
      else proximo.add(fase);
      return proximo;
    });

  const executarDescongelar = async () => {
    try {
      aplicarAmbiente(await descongelarAmbiente(request, id));
      toast.success("Ambiente descongelado.");
    } catch {
      // Mensagem do backend já saiu no auto-toast do request.
    }
  };

  // Lote D (I-2): o relógio do buffer pausa (ou não) conforme o MOTIVO do
  // congelamento — política na chave `motivos_congelamento_pausam` da
  // Config. Sem a chave (backend atrás / sem ppcp:view), degrade: badge
  // fica só "Congelado", sem inventar política.
  const mapaPausaCongelamento = valorConfigDe(
    "motivos_congelamento_pausam", null,
  );
  let relogioPausado = null;
  if (
    ambiente?.congelado
    && mapaPausaCongelamento
    && typeof mapaPausaCongelamento === "object"
    && ambiente.congelamento_motivo in mapaPausaCongelamento
  ) {
    relogioPausado = Boolean(
      mapaPausaCongelamento[ambiente.congelamento_motivo],
    );
  }

  // ---- EntityHeader: badges + ações ----
  const badges = [];
  if (ambiente) {
    badges.push({
      key: "fase",
      label: FASE_INFO[ambiente.fase]?.rotulo || ambiente.fase_display,
      tone: "accent",
    });
    if (ambiente.congelado) {
      badges.push({
        key: "congelado",
        label:
          relogioPausado === null
            ? "Congelado"
            : relogioPausado
              ? "Congelado · relógio pausado"
              : "Congelado · relógio corre",
        tone: "warning",
      });
    }
    if (ambiente.cancelado) {
      badges.push({ key: "cancelado", label: "Cancelado", tone: "danger" });
    }
  }

  const acoesHeader = [];
  if (ambiente && !ambiente.cancelado) {
    if (podeRegistrarCausa && ambiente.fase !== FASE_CARREGADO) {
      acoesHeader.push({
        key: "atraso",
        label: "Registrar atraso",
        icon: <FiClock size={14} />,
        primary: true,
        onClick: () => setPainelAcao("atraso"),
      });
    }
    if (ambiente.congelado && podeDescongelar) {
      acoesHeader.push({
        key: "descongelar",
        label: "Descongelar",
        icon: <FiSun size={14} />,
        onClick: executarDescongelar,
      });
    }
    if (!ambiente.congelado && podeCongelar
        && ambiente.fase !== FASE_CARREGADO) {
      acoesHeader.push({
        key: "congelar",
        label: "Congelar",
        icon: <FiCloudSnow size={14} />,
        onClick: () => setPainelAcao("congelar"),
      });
    }
    if (podeCancelar) {
      acoesHeader.push({
        key: "cancelar",
        label: "Cancelar ambiente",
        icon: <FiXCircle size={14} />,
        danger: true,
        onClick: () => setPainelAcao("cancelar"),
      });
    }
  }

  // ---- KPIs ----
  const itensKpi = useMemo(() => {
    if (!ambiente) return [];
    const buffer = ambiente.buffer || {};
    const zona = buffer.zona || "verde";
    // Lote D: decomposição do relógio real quando o Detalhe expõe
    // `buffer_detalhe {decorrido_min, pausado_min, baseline_min}` —
    // vira tooltip do card. Sem o campo (backend atrás), sem tooltip.
    const detalheBuffer = ambiente.buffer_detalhe || buffer.detalhe || null;
    const tituloBuffer =
      detalheBuffer && detalheBuffer.baseline_min !== undefined
        ? `decorrido ${formatarHorasMinutos(detalheBuffer.decorrido_min, "0min")}`
          + ` · pausado ${formatarHorasMinutos(detalheBuffer.pausado_min, "0min")}`
          + ` · baseline ${formatarHorasMinutos(detalheBuffer.baseline_min, "0min")}`
        : undefined;
    return [
      {
        label: "Fase atual",
        value: FASE_INFO[ambiente.fase]?.curto || ambiente.fase_display,
        sub: derivado.proxima
          ? `aguardando ${derivado.proxima.label}`
          : "todas as etapas concluídas",
      },
      {
        label: "Progresso",
        value: `${derivado.concluidas}/${derivado.aplicaveis.length}`,
        sub: `${ambiente.progresso_pct ?? 0}% das etapas aplicáveis`,
      },
      {
        label: "Buffer do ambiente",
        value: buffer.total_min ? `${buffer.pct}%` : "–",
        tone: BUFFER_ZONA_TONES[zona],
        title: tituloBuffer,
        sub: buffer.total_min
          ? `${formatarMinutosAtraso(buffer.consumido_min, "0m")} de ${formatarMinutosAtraso(buffer.total_min, "0m")} · zona ${zona}`
          : "buffer não definido",
      },
      {
        label: "Atraso acumulado",
        value: formatarMinutosAtraso(derivado.totalAtrasoMin),
        tone: derivado.totalAtrasoMin > 0 ? "danger" : "neutral",
        sub: `${(ambiente.atrasos || []).length} registro(s)`,
      },
      {
        label: "Peças",
        value: ambiente.pecas_total ?? 0,
        sub: "OPs vinculadas ao ambiente",
      },
    ];
  }, [ambiente, derivado]);

  // ---- estados de carga ----
  if (carregando) {
    return (
      <PageContainer>
        <EntityHeader
          onBack={() => navigate("/producao")}
          backLabel="Painel"
          title="Carregando ambiente…"
        />
      </PageContainer>
    );
  }

  if (erro || !ambiente) {
    return (
      <PageContainer>
        <EntityHeader
          onBack={() => navigate("/producao")}
          backLabel="Painel"
          title="Detalhe do Ambiente"
        />
        <LinhaErro>
          <ErrorMsg style={{ flex: 1 }}>
            {erro || "Ambiente não encontrado."}
          </ErrorMsg>
          <SecondaryButton type="button" onClick={carregar}>
            Tentar de novo
          </SecondaryButton>
        </LinhaErro>
      </PageContainer>
    );
  }

  const dataAlvoOriginal = ambiente.data_alvo_carregamento || "";
  // Estado do ambiente permite operar; a permissão é por fase (por etapa).
  const podeOperarEtapas = !acoesBloqueadas;

  const mensagemErroEtapa = (err, etapa, acao) => {
    if (erroEhCheckpointBipagem(err)) {
      return `«${etapa.label}» é um checkpoint de bipagem — a conclusão `
        + "acontece peça a peça no Terminal, não por aqui.";
    }
    return err?.payload?.detalhe || err?.message
      || `Não foi possível ${acao} a etapa ${etapa.label}.`;
  };

  return (
    <PaginaDetalhe>
      <EntityHeader
        onBack={() => navigate("/producao")}
        backLabel="Painel"
        title={ambiente.nome}
        subtitle={`Produção / ${ambiente.pedido_numero} / ${ambiente.numero}`}
        badges={badges}
        actions={acoesHeader}
      />

      {ambiente.congelado && (
        <WarningMsg style={{ alignItems: "flex-start" }}>
          <BannerConteudo>
            <strong>
              <FiCloudSnow size={14} aria-hidden="true" />
              Ambiente congelado — {ambiente.congelamento_motivo_display
                || ambiente.congelamento_motivo}
            </strong>
            <span>
              Desde {formatarDataHoraCurta(ambiente.congelado_em)} · vence{" "}
              {ambiente.congelado_ate
                ? formatarDataCurta(ambiente.congelado_ate)
                : "sem prazo"}
              {" · por "}
              {ambiente.congelado_por_nome || "–"}
            </span>
            {relogioPausado !== null && (
              <span>
                {relogioPausado
                  ? "Relógio do buffer: pausado enquanto congelado (o tempo parado não consome buffer)."
                  : "Relógio do buffer: segue correndo — este motivo não pausa o consumo."}
              </span>
            )}
            {ambiente.congelamento_observacao && (
              <span>“{ambiente.congelamento_observacao}”</span>
            )}
          </BannerConteudo>
        </WarningMsg>
      )}

      {ambiente.cancelado && (
        <ErrorMsg>
          <BannerConteudo>
            <strong>
              <FiAlertTriangle size={14} aria-hidden="true" />
              Ambiente cancelado em {formatarDataHoraCurta(ambiente.cancelado_em)}
            </strong>
            {ambiente.motivo_cancelamento && (
              <span>Motivo: {ambiente.motivo_cancelamento}</span>
            )}
          </BannerConteudo>
        </ErrorMsg>
      )}

      <KpiStrip items={itensKpi} />

      <Card>
        <SectionTitle>Dados de produção</SectionTitle>
        <LinhaDado>
          <RotuloCampo>Data-alvo de carregamento</RotuloCampo>
          {podeEditar && !ambiente.cancelado ? (
            <>
              <CampoInput
                type="date"
                value={dataAlvo}
                onChange={(e) => setDataAlvo(e.target.value)}
                aria-label="Data-alvo de carregamento"
              />
              {dataAlvo !== dataAlvoOriginal && (
                <AsyncButton
                  variant="secondary"
                  silentError
                  successMessage="Data-alvo atualizada."
                  onClick={async () => {
                    aplicarAmbiente(
                      await atualizarAmbiente(request, ambiente.id, {
                        data_alvo_carregamento: dataAlvo || null,
                      }),
                    );
                  }}
                >
                  Salvar
                </AsyncButton>
              )}
            </>
          ) : (
            <Mono>{formatarDataCurta(ambiente.data_alvo_carregamento)}</Mono>
          )}
        </LinhaDado>
        <LinhaDado>
          <RotuloCampo>Carregado em</RotuloCampo>
          <Mono>{formatarDataHoraCurta(ambiente.carregado_em)}</Mono>
          <RotuloCampo style={{ marginLeft: 16 }}>Responsável</RotuloCampo>
          <span>{ambiente.responsavel_nome || "–"}</span>
        </LinhaDado>
      </Card>

      <Card>
        <SectionTitle>Linha do tempo — fases e etapas</SectionTitle>
        {erroEtapa && (
          <WarningMsg style={{ marginBottom: 10 }}>{erroEtapa}</WarningMsg>
        )}
        <TabelaBloco>
          <colgroup>
            <col style={{ width: "13%" }} />
            <col style={{ width: "27%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "20%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Estado</th>
              <th>Etapa</th>
              <th>Responsável</th>
              <th>Início</th>
              <th>Conclusão</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {derivado.grupos.map((grupo) => {
              const aberta = !fasesFechadas.has(grupo.fase);
              return (
                <React.Fragment key={grupo.fase}>
                  <tr className="fase" onClick={() => alternarFase(grupo.fase)}>
                    <td colSpan={6}>
                      {aberta ? (
                        <FiChevronDown size={12} aria-hidden="true" />
                      ) : (
                        <FiChevronRight size={12} aria-hidden="true" />
                      )}{" "}
                      {grupo.rotulo}
                      <ResumoFase>
                        {grupo.aplicaveis > 0
                          ? `${grupo.concluidas}/${grupo.aplicaveis} concluídas`
                          : "não se aplica"}
                      </ResumoFase>
                    </td>
                  </tr>
                  {aberta &&
                    grupo.etapas.map((etapa) => {
                      const na = etapa.estado === ESTADO_NAO_APLICAVEL;
                      const ehCheckpoint = checkpoints.has(etapa.codigo);
                      const operavel =
                        etapa.estado === ESTADO_PENDENTE
                        || etapa.estado === ESTADO_EM_EXECUCAO;
                      // Lote B: botão por FASE (D1.1) e checkpoint conclui
                      // só pela bipagem no Terminal (§2.5).
                      const mostrarAcoes =
                        podeOperarEtapas
                        && operavel
                        && !ehCheckpoint
                        && podeOperarFaseDe(etapa.fase);
                      const mostrarPillCheckpoint =
                        podeOperarEtapas && operavel && ehCheckpoint;
                      const permExtraConcluir = permissaoExtraConcluirEtapa(etapa.codigo);
                      const podeConcluir =
                        !permExtraConcluir || hasPermission(permExtraConcluir);
                      return (
                        <tr key={etapa.id} className={na ? "na" : undefined}>
                          <td>
                            <Pill $tone={ESTADO_ETAPA_TONES[etapa.estado]}>
                              {ESTADO_ETAPA_LABELS[etapa.estado] || etapa.estado}
                            </Pill>
                          </td>
                          <td
                            title={
                              ehCheckpoint
                                ? `${etapa.label} — checkpoint de bipagem (conclui peça a peça no Terminal)`
                                : etapa.bipavel
                                  ? `${etapa.label} — etapa de chão de fábrica`
                                  : etapa.label
                            }
                          >
                            {etapa.label}
                          </td>
                          <td
                            data-rotulo="Responsável"
                            title={etapa.responsavel_nome || ""}
                          >
                            {etapa.responsavel_nome || "–"}
                          </td>
                          <td data-rotulo="Início">
                            <Mono>{formatarDataHoraCurta(etapa.iniciada_em)}</Mono>
                          </td>
                          <td data-rotulo="Conclusão">
                            <Mono>
                              {formatarDataHoraCurta(etapa.concluida_em)}
                            </Mono>
                          </td>
                          <td className="acoes">
                            {mostrarPillCheckpoint && (
                              <Pill
                                $tone="neutral"
                                title="Etapa-checkpoint: inicia e conclui peça a peça pela bipagem no Terminal — sem conclusão manual"
                              >
                                conclui por bipagem
                              </Pill>
                            )}
                            {mostrarAcoes && (
                              <AcoesLinha>
                                {etapa.estado === ESTADO_PENDENTE && (
                                  <AsyncButton
                                    variant="secondary"
                                    silentError
                                    disabled={ambiente.congelado}
                                    title={
                                      ambiente.congelado
                                        ? "Descongele o ambiente antes de operar etapas"
                                        : undefined
                                    }
                                    successMessage={`Etapa ${etapa.label} iniciada.`}
                                    onClick={async () => {
                                      setErroEtapa(null);
                                      try {
                                        aplicarAmbiente(
                                          await iniciarEtapa(
                                            request, ambiente.id, etapa.id,
                                            { silentError: true },
                                          ),
                                        );
                                      } catch (err) {
                                        setErroEtapa(
                                          mensagemErroEtapa(err, etapa, "iniciar"),
                                        );
                                        throw err;
                                      }
                                    }}
                                  >
                                    Iniciar
                                  </AsyncButton>
                                )}
                                {podeConcluir && (
                                <AsyncButton
                                  variant="secondary"
                                  silentError
                                  disabled={ambiente.congelado}
                                  title={
                                    ambiente.congelado
                                      ? "Descongele o ambiente antes de operar etapas"
                                      : undefined
                                  }
                                  successMessage={`Etapa ${etapa.label} concluída.`}
                                  onClick={async () => {
                                    setErroEtapa(null);
                                    try {
                                      aplicarAmbiente(
                                        await concluirEtapa(
                                          request, ambiente.id, etapa.id,
                                          { silentError: true },
                                        ),
                                      );
                                    } catch (err) {
                                      setErroEtapa(
                                        mensagemErroEtapa(err, etapa, "concluir"),
                                      );
                                      throw err;
                                    }
                                  }}
                                >
                                  Concluir
                                </AsyncButton>
                                )}
                              </AcoesLinha>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </tbody>
        </TabelaBloco>
      </Card>

      <Card>
        <SectionTitle>Atrasos por etapa e causa</SectionTitle>
        {(ambiente.atrasos || []).length === 0 ? (
          <EmptyMsg>Nenhum atraso registrado neste ambiente.</EmptyMsg>
        ) : (
          <TabelaBloco>
            <colgroup>
              <col style={{ width: "18%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "28%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "15%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Etapa</th>
                <th>Minutos</th>
                <th>Categoria</th>
                <th>Descrição</th>
                <th>Registrado</th>
                <th>Por</th>
              </tr>
            </thead>
            <tbody>
              {ambiente.atrasos.map((atraso) => (
                <tr key={atraso.id}>
                  <td
                    data-rotulo="Etapa"
                    title={
                      derivado.rotuloEtapa[atraso.etapa_codigo]
                      || "Ambiente inteiro"
                    }
                  >
                    {derivado.rotuloEtapa[atraso.etapa_codigo]
                      || "Ambiente inteiro"}
                  </td>
                  <td data-rotulo="Atraso">
                    <Mono>{atraso.minutos} min</Mono>
                  </td>
                  <td>
                    <Pill $tone="neutral">
                      {atraso.categoria_display || atraso.categoria}
                    </Pill>
                  </td>
                  <td title={atraso.descricao || ""}>
                    {atraso.descricao || "–"}
                  </td>
                  <td data-rotulo="Registrado">
                    <Mono>{formatarDataHoraCurta(atraso.registrado_em)}</Mono>
                  </td>
                  <td data-rotulo="Por" title={atraso.registrado_por_nome || ""}>
                    {atraso.registrado_por_nome || "–"}
                  </td>
                </tr>
              ))}
            </tbody>
          </TabelaBloco>
        )}
      </Card>

      {/* P3 — blocos reais de Peças + Bipagens por posto (com slide-over
          da peça: genealogia, cancelar OP e registrar reposição). */}
      <PecasDoAmbiente
        ambiente={ambiente}
        onRecarregarAmbiente={carregar}
      />

      {/* BOM estimado × executivo com delta pelos 3 sinais reais (I-7). */}
      <BomBloco ambienteId={ambiente.id} gatilhoRecarga={ambiente} />

      {/* Linha do tempo agregada (auditoria + bipagens agrupadas + atrasos
          + marcos do lote) — recarrega quando uma action troca o ambiente. */}
      <Timeline ambienteId={ambiente.id} gatilhoRecarga={ambiente} />

      <CongelarForm
        aberto={painelAcao === "congelar"}
        onFechar={() => setPainelAcao(null)}
        ambienteId={ambiente.id}
        onAtualizado={aplicarAmbiente}
      />
      <RegistrarAtrasoForm
        aberto={painelAcao === "atraso"}
        onFechar={() => setPainelAcao(null)}
        ambienteId={ambiente.id}
        etapas={derivado.aplicaveis}
        onAtualizado={aplicarAmbiente}
      />
      <CancelarForm
        aberto={painelAcao === "cancelar"}
        onFechar={() => setPainelAcao(null)}
        ambiente={ambiente}
        onAtualizado={aplicarAmbiente}
      />
    </PaginaDetalhe>
  );
}
